import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { getYingshiDeviceList } from '@/service/yingshi'

/** 落库 query 去掉 i2antdV5 / axios 客户端字段 */
function toDeviceListStoreQuery (query = {}) {
  if (!query || typeof query !== 'object') return {}
  const next = { ...query }
  delete next.killPending
  delete next.refQ
  return next
}

function normalizeApiCatchError (error) {
  if (error == null) return '获取设备列表失败'
  if (typeof error === 'string') return error
  const d = error.response && error.response.data
  if (d != null) {
    if (typeof d === 'string') return d
    if (typeof d === 'object' && (d.msg != null || d.message != null)) {
      return String(d.msg != null && d.msg !== '' ? d.msg : d.message)
    }
  }
  if (typeof error === 'object' && (error.msg != null || error.message != null)) {
    const m = error.msg != null && error.msg !== '' ? error.msg : error.message
    if (m != null && m !== '') return String(m)
  }
  if (error.message) return String(error.message)
  return '获取设备列表失败'
}

/** @param {*} res Api 成功响应 */
export function extractYingshiDeviceListFromResponse (res) {
  const pickArray = (node) => {
    if (Array.isArray(node)) return node
    if (node == null || typeof node !== 'object') return null
    const keys = ['data', 'list', 'records', 'rows', 'content', 'items', 'result', 'devices', 'deviceList']
    for (let i = 0; i < keys.length; i += 1) {
      const val = node[keys[i]]
      if (Array.isArray(val)) return val
    }
    return null
  }
  return pickArray(res?.data) || pickArray(res) || []
}

const initialState = {
  fetching: false,
  error: null,
  query: {},
  data: [],
  requestId: ''
}

export const fetchYingshiDeviceList = createAsyncThunk(
  'yingshiDeviceList/fetchYingshiDeviceList',
  async ({ query = {} } = {}, { rejectWithValue }) => {
    try {
      const res = await getYingshiDeviceList(query)
      return {
        data: extractYingshiDeviceListFromResponse(res),
        query: toDeviceListStoreQuery(query)
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ __cancelled: true })
      }
      return rejectWithValue(normalizeApiCatchError(error))
    }
  },
  {
    condition: ({ query = {} } = {}, { getState }) => {
      const s = getState().yingshi.deviceList
      if (!s.fetching) return true
      const nextStoreId = String(query?.storeId ?? '')
      const currStoreId = String(s.query?.storeId ?? '')
      if (nextStoreId === currStoreId) return false
      return true
    }
  }
)

const deviceListSlice = createSlice({
  name: 'yingshiDeviceList',
  initialState,
  reducers: {
    resetYingshiDeviceListState: () => initialState,
    patchYingshiDeviceNeedInference: (state, action) => {
      const { id, needInference } = action.payload || {}
      if (id == null || id === '') return
      const idx = state.data.findIndex((d) => d.id === id || String(d.id) === String(id))
      if (idx >= 0) {
        state.data[idx].needInference = needInference
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchYingshiDeviceList.pending, (state, action) => {
        state.fetching = true
        state.error = null
        state.requestId = action.meta.requestId
        const nextQuery = toDeviceListStoreQuery(action.meta.arg?.query)
        const prevStoreId = String(state.query?.storeId ?? '')
        const nextStoreId = String(nextQuery?.storeId ?? '')
        state.query = nextQuery
        // 切店：立刻清空旧店设备，避免监控墙在 fulfilled 前仍用旧 list 当新店
        if (prevStoreId !== nextStoreId) {
          state.data = []
        }
      })
      .addCase(fetchYingshiDeviceList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return
        state.fetching = false
        state.error = null
        const payloadData = action.payload?.data
        state.data = Array.isArray(payloadData)
          ? payloadData
          : extractYingshiDeviceListFromResponse({ data: payloadData })
        state.query = action.payload?.query || {}
      })
      .addCase(fetchYingshiDeviceList.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return
        if (action.payload && action.payload.__cancelled) return
        state.fetching = false
        state.error = action.payload
        state.data = []
      })
  }
})

export const { resetYingshiDeviceListState, patchYingshiDeviceNeedInference } = deviceListSlice.actions
export default deviceListSlice.reducer
